import { supabase, Quote, QuoteWithRelations, QUOTE_STATUS } from '@cleanpro/common-ui';
import type { Database } from '@cleanpro/common-ui';
import { transformQuote, transformCustomer, transformRequest, transformProperty, transformBusiness, transformJob, transformService } from './transformers';

type QuoteRow = Database['public']['Tables']['quotes']['Row'];
type CustomerRow = Database['public']['Tables']['customers']['Row'];
type RequestRow = Database['public']['Tables']['requests']['Row'];
type PropertyRow = Database['public']['Tables']['properties']['Row'];
type BusinessRow = Database['public']['Tables']['businesses']['Row'];
type JobRow = Database['public']['Tables']['jobs']['Row'];
type ServiceRow = Database['public']['Tables']['services']['Row'];

/**
 * Type for a quote query result with all possible relations
 * Note: Supabase returns singular relations as single objects and plural as arrays
 */
type QuoteWithRelationsRow = QuoteRow & {
  customers?: CustomerRow | null;
  requests?: RequestRow | null;
  businesses?: BusinessRow | null;
  jobs?: JobRow | JobRow[] | null;
  properties?: PropertyRow | null;
  services?: ServiceRow | null;
};

/**
 * Transforms a database quote row with relations to the QuoteWithRelations interface
 * Uses transformers to convert database rows to proper entity interfaces
 */
const transformQuoteWithRelations = (row: QuoteWithRelationsRow): QuoteWithRelations => {
  const quote = transformQuote(row);
  
  // Handle jobs - could be a single job or array, we take the first if array
  const jobRow = Array.isArray(row.jobs) ? row.jobs[0] : row.jobs;
  
  return {
    ...quote,
    customer: transformCustomer(row.customers),
    request: transformRequest(row.requests),
    business: transformBusiness(row.businesses),
    job: transformJob(jobRow),
    property: transformProperty(row.properties),
    service: transformService(row.services),
  };
};

/**
 * Fetches all quotes with related customer and request data
 */
export const fetchQuotes = async (): Promise<QuoteWithRelations[]> => {
  const { data, error } = await supabase
    .from('quotes')
    .select(`
      *,
      customers (*),
      requests (*),
      services (*),
      businesses!quotes_business_id_fkey (*),
      jobs (*),
      properties (*)
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(transformQuoteWithRelations);
};

/**
 * Fetches a single quote by ID with all related data
 */
export const fetchQuoteById = async (quoteId: string): Promise<QuoteWithRelations | null> => {
  const { data, error } = await supabase
    .from('quotes')
    .select(`
      *,
      customers (*),
      requests (*),
      services (*),
      businesses!quotes_business_id_fkey (*),
      jobs (*),
      properties (*)
    `)
    .eq('id', quoteId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Not found
    }
    throw error;
  }
  console.log('Fetched quote data:', data);
  return data ? transformQuoteWithRelations(data) : null;
};

/**
 * Creates a new quote in the database
 */
export const createQuote = async (
  quote: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Quote> => {
  const now = new Date().toISOString();
  
  const { data, error } = await supabase
    .from('quotes')
    .insert({
      accepted_at: quote.acceptedAt,
      cancelled_at: quote.cancelledAt,
      created_at: now,
      customer_id: quote.customerId,
      customer_notes: quote.customerNotes,
      discount_amount: quote.discountAmount,
      internal_notes: quote.internalNotes,
      line_items: JSON.stringify(quote.lineItems),
      quote_number: quote.quoteNumber,
      rejected_at: quote.rejectedAt,
      request_id: quote.requestId,
      required_deposit: quote.requiredDeposit,
      responded_at: quote.respondedAt,
      sent_at: quote.sentAt,
      status: quote.status || QUOTE_STATUS.DRAFT,
      subtotal: quote.subtotal,
      tax_amount: quote.taxAmount,
      tax_rate: quote.taxRate,
      terms: quote.terms,
      total_amount: quote.totalAmount,
      updated_at: now,
      valid_until: quote.validUntil,
      viewed_at: quote.viewedAt,
      is_exclusive: quote.isExclusive || false,
      exclusive_expired_at: quote.exclusiveExpiredAt,
    })
    .select(`
      *,
      customers (
        id,
        name,
        email
      )
    `)
    .single();

  if (error) throw error;
  return transformQuote(data);
};

/**
 * Maps Quote fields from camelCase to snake_case for database operations
 */
const mapQuoteFieldsToDatabase = (
  quoteData: Partial<Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>>
): Record<string, unknown> => {
  const updateData: Record<string, unknown> = {};

  if (quoteData.acceptedAt !== undefined) updateData.accepted_at = quoteData.acceptedAt;
  if (quoteData.cancelledAt !== undefined) updateData.cancelled_at = quoteData.cancelledAt;
  if (quoteData.customerId !== undefined) updateData.customer_id = quoteData.customerId;
  if (quoteData.customerNotes !== undefined) updateData.customer_notes = quoteData.customerNotes;
  if (quoteData.discountAmount !== undefined) updateData.discount_amount = quoteData.discountAmount;
  if (quoteData.internalNotes !== undefined) updateData.internal_notes = quoteData.internalNotes;
  if (quoteData.lineItems !== undefined) updateData.line_items = JSON.stringify(quoteData.lineItems);
  if (quoteData.quoteNumber !== undefined) updateData.quote_number = quoteData.quoteNumber;
  if (quoteData.rejectedAt !== undefined) updateData.rejected_at = quoteData.rejectedAt;
  if (quoteData.requestId !== undefined) updateData.request_id = quoteData.requestId;
  if (quoteData.requiredDeposit !== undefined) updateData.required_deposit = quoteData.requiredDeposit;
  if (quoteData.respondedAt !== undefined) updateData.responded_at = quoteData.respondedAt;
  if (quoteData.sentAt !== undefined) updateData.sent_at = quoteData.sentAt;
  if (quoteData.status !== undefined) updateData.status = quoteData.status;
  if (quoteData.subtotal !== undefined) updateData.subtotal = quoteData.subtotal;
  if (quoteData.taxAmount !== undefined) updateData.tax_amount = quoteData.taxAmount;
  if (quoteData.taxRate !== undefined) updateData.tax_rate = quoteData.taxRate;
  if (quoteData.terms !== undefined) updateData.terms = quoteData.terms;
  if (quoteData.totalAmount !== undefined) updateData.total_amount = quoteData.totalAmount;
  if (quoteData.validUntil !== undefined) updateData.valid_until = quoteData.validUntil;
  if (quoteData.viewedAt !== undefined) updateData.viewed_at = quoteData.viewedAt;
  if (quoteData.isExclusive !== undefined) updateData.is_exclusive = quoteData.isExclusive;
  if (quoteData.exclusiveExpiredAt !== undefined) updateData.exclusive_expired_at = quoteData.exclusiveExpiredAt;

  return updateData;
};

/**
 * Updates an existing quote with partial data
 */
export const updateQuote = async (
  quoteId: string, 
  quoteData: Partial<Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<Quote> => {
  const now = new Date().toISOString();
  
  // Build update object with proper snake_case mapping
  const updateData = {
    ...mapQuoteFieldsToDatabase(quoteData),
    updated_at: now,
  };

  const { data, error } = await supabase
    .from('quotes')
    .update(updateData)
    .eq('id', quoteId)
    .select(`
      *,
      customers (
        id,
        name,
        email
      )
    `)
    .single();

  if (error) throw error;
  return transformQuote(data);
};

